// BrokerCard — the "NGUỒN PHỤ TRÁCH" (assigned-broker / Source) row with
// call + message actions. The Source is the broker who liaises with the real
// property owner; an agent viewing the listing uses this card to reach them
// quickly for more info. Renders inside a tinted card on mobile (inside the
// bottom-sheet) and inline-on-background on desktop (pinned to the foot of the
// right column).
//
// Name + phone arrive resolved on the property detail response
// (assignedBrokerName / assignedBrokerPhoneNumber). The call button dials the
// number; the desktop message button opens a Zalo chat. Both fall back to a
// disabled state when no phone is on file.

import { MessageCircle, Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Property } from "@/types/property";

interface BrokerCardProps {
  property: Property;
  variant?: "mobile" | "desktop";
}

/** Initials from a full name — first + last word initial, else first two chars. */
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter((s) => s.length > 0);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")).toUpperCase();
}

export function BrokerCard({
  property,
  variant = "mobile",
}: BrokerCardProps) {
  const isMobile = variant === "mobile";
  const brokerId = property.assignedBrokerId;
  const name = property.assignedBrokerName ?? null;
  const phone = property.assignedBrokerPhoneNumber ?? null;

  const hasBroker = brokerId !== null;
  const displayName = name ?? (hasBroker ? `Nguồn #${brokerId}` : "Chưa gán nguồn");
  const initials = name !== null ? initialsFromName(name) : "—";

  return (
    <div
      className={cn(
        "flex items-center gap-3",
        isMobile
          ? "rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-soft)]"
          : "",
      )}
    >
      <div
        aria-hidden
        className={cn(
          "flex shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-primary to-foreground font-mono font-semibold text-primary-foreground",
          isMobile ? "size-9 text-[12px]" : "size-[38px] text-[12px]",
        )}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
          Nguồn phụ trách
        </div>
        <div className="mt-0.5 truncate text-[14px] font-semibold">
          {displayName}
        </div>
        {hasBroker ? (
          <div
            className={cn(
              "font-mono text-[12px]",
              phone !== null ? "text-muted-foreground" : "text-ink-faint",
            )}
          >
            {phone ?? "Chưa có số điện thoại"}
          </div>
        ) : null}
      </div>

      {/* Call — dials the Source. Disabled (not hidden) with no phone so the
          card's action affordance stays put. */}
      {phone !== null ? (
        <Button
          asChild
          variant="outline"
          size="icon-sm"
          aria-label={`Gọi ${displayName}`}
          className="rounded-[9px]"
        >
          <a href={`tel:${phone}`}>
            <Phone aria-hidden="true" className="size-3.5" />
          </a>
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Chưa có số điện thoại"
          className="rounded-[9px]"
          disabled
        >
          <Phone aria-hidden="true" className="size-3.5" />
        </Button>
      )}

      {/* Message via Zalo — desktop only, and only when we have a number to
          route to (chat.zalo.me takes over in the installed client). */}
      {!isMobile && phone !== null ? (
        <Button
          asChild
          variant="outline"
          size="icon-sm"
          aria-label={`Nhắn Zalo cho ${displayName}`}
          className="rounded-[9px]"
        >
          <a
            href={`https://chat.zalo.me/${phone}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle aria-hidden="true" className="size-3.5" />
          </a>
        </Button>
      ) : null}
    </div>
  );
}
